
import React, { createContext, useContext, useState, useEffect } from "react";
import { Settings, defaultSettings } from "@/types/settings";
import { supabase } from "@/integrations/supabase/client";
import { fetchSettingsFromDB, saveSettingsToCache, loadSettingsFromCache } from "@/utils/settingsUtils";

// Créer le contexte avec des valeurs par défaut
export const SettingsContext = createContext<{
  settings: Settings | null;
  loading: boolean;
  error: Error | null;
  refreshSettings: () => Promise<Settings | null>;
}>({
  settings: defaultSettings,
  loading: false,
  error: null,
  refreshSettings: async () => null,
});

// Provider du composant 
export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [settings, setSettings] = useState<Settings | null>(defaultSettings);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Fonction pour charger les paramètres
  const fetchSettings = async (): Promise<Settings | null> => {
    setLoading(true);
    try {
      const fetchedSettings = await fetchSettingsFromDB();
      
      if (fetchedSettings) {
        setSettings(fetchedSettings);
        saveSettingsToCache(fetchedSettings);
        return fetchedSettings;
      } else {
        setSettings(defaultSettings);
        return defaultSettings;
      }
    } catch (err: any) {
      setError(err);
      
      // Essayer de charger depuis le cache si la requête DB échoue
      const cachedSettings = loadSettingsFromCache();
      setSettings(cachedSettings);
      return cachedSettings;
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour rafraîchir les paramètres
  const refreshSettings = async (): Promise<Settings | null> => {
    return fetchSettings();
  };

  // Chargement initial
  useEffect(() => {
    fetchSettings();
    
    // Configuration de l'écouteur realtime
    const channel = supabase.channel('settings-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'settings' },
        () => {
          fetchSettings();
        }
      )
      .subscribe();
      
    // Nettoyage lors du démontage
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        error,
        refreshSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const useSettingsContext = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettingsContext doit être utilisé dans un SettingsProvider");
  }
  return context;
};
